import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { prepareHeaders } from '@/configuration/tokenHandling/prepareHeaders';
import { BrakeSortBy } from '@/features/brakes/hooks/useBrakesQueryParams';
import { CommonSortBy } from '@/features/common/hooks/useCommonQueryParams';
import { SortDirection } from '@/features/common/hooks/useSorting';
import { BrakePageResponse, GroupedBrakePageResponse } from '@/generated/GeneratedApiTypes';

export interface CommonQueryParams<S = CommonSortBy> {
    pageSize: number | undefined | null;
    pageNumber: number | undefined | null;
    sortBy: S | undefined | null;
    sortDirection: SortDirection | undefined | null;
    assetIds: string[] | undefined | null;
    workshopIds: string[] | undefined | null;
    accountIds: string[] | undefined | null;
}

export interface GetBrakesRequestParams extends CommonQueryParams<BrakeSortBy> {
    min: number | undefined;
    max: number | undefined;
    status: string[] | undefined;
}

export const API_PATH_PREFIX = '/vehicle-diagnostics';
const filenameRegexMatcher = /filename="(.+\.[a-z]{2,})"/gi;

const arrayOrUndefined = <T>(arr: T[] | undefined | null) => (arr && arr.length > 0 ? arr : undefined);

const convertCommonParams = (params: CommonQueryParams) => ({
    page_number: params.pageNumber || undefined,
    page_size: params.pageSize || undefined,
    sort_by: params.sortBy || undefined,
    sort_direction: params.sortDirection || undefined,
    asset_ids: arrayOrUndefined(params.assetIds),
    workshop_ids: arrayOrUndefined(params.workshopIds),
    account_ids: arrayOrUndefined(params.accountIds),
});

export const vehicleDiagnosticsApi = createApi({
    reducerPath: 'vehicleDiagnosticsApi',
    baseQuery: fetchBaseQuery({ baseUrl: `${import.meta.env.VITE_BACKEND_URI}`, prepareHeaders }),
    endpoints: (builder) => ({
        getGroupedBrakes: builder.query<GroupedBrakePageResponse, GetBrakesRequestParams>({
            query: (allParams: GetBrakesRequestParams) => {
                const convertedParams = {
                    ...convertCommonParams(allParams as CommonQueryParams),
                    min: allParams.min || undefined,
                    max: allParams.max || undefined,
                    status: arrayOrUndefined(allParams.status),
                };
                return {
                    url: `${API_PATH_PREFIX}/brakes/grouped`,
                    params: { ...convertedParams },
                };
            },
        }),
        downloadCsvFile: builder.mutation({
            query: (allParams: GetBrakesRequestParams) => ({
                url: `${API_PATH_PREFIX}/export/brakes`,
                params: {
                    ...convertCommonParams(allParams as CommonQueryParams),
                    min: allParams.min || undefined,
                    max: allParams.max || undefined,
                    status: arrayOrUndefined(allParams.status),
                },
                responseHandler: async (response) => {
                    try {
                        if (!response.ok) {
                            return {
                                isSuccess: false,
                                reason: response.statusText,
                            };
                        }
                        const defaultFilename = `${new Date().toISOString().split('.')[0]}.csv`;

                        const data = await response.blob();
                        const contentDisposition = response.headers.get('Content-Disposition') ?? 'NO_HEADER';
                        const filename = filenameRegexMatcher.exec(contentDisposition)?.[1] ?? defaultFilename;

                        const url = window.URL.createObjectURL(data);
                        const anchor = document.createElement('a');
                        anchor.href = url;
                        anchor.download = filename;
                        document.body.append(anchor);
                        anchor.click();
                        anchor.remove();
                        window.URL.revokeObjectURL(url);
                        return {
                            isSuccess: true,
                        };
                    } catch (e) {
                        return {
                            isSuccess: false,
                        };
                    }
                },
            }),
        }),
    }),
});

export const { useGetGroupedBrakesQuery, useDownloadCsvFileMutation } = vehicleDiagnosticsApi;
